import { GetProps, SizableText, styled, YStack } from '@mythical/ui'
import {
  AllSeriesContentBlocks,
  HomepageContentBlock,
  LandingContentBlock,
  MerchPageContentBlock,
  PageContentBlock,
  PostContentBlock,
} from 'app/blocks'
import { BannerBlock } from 'app/components/blocks/Banner'
import getEnv from 'app/lib/env-getter'
import { CollectionType } from 'app/queries/collections/collection-fragment'
import { mapExcludedPlatforms } from 'app/utils/mapExcludedPlatforms'
import React from 'react'
import { Platform } from 'react-native'

import { FormBlock } from 'app/components/blocks/Form'
import { ContentCardProps } from 'app/components/content/ContentCard'
import { ImageBlock } from '../blocks/Image'
import { ImageCarouselBlock } from '../blocks/ImageCarousel'
import { RichTextBlock } from '../blocks/RichText'
import { VideoBlock } from '../blocks/Video'
import { RichTextProps } from '../primitive/RichText'
import { ButtonBlock } from './Button'
import { CallToAction } from './CallToAction'
import { FAQsBlock } from './FAQs'
import { ComingUpBlock } from './homepage/ComingUp'
import { ContinueWatchingBlock } from './homepage/ContinueWatching'
import { GifOfTheDayBlock } from './homepage/GifOfTheDay'
import { HomepageContentCarouselBlock } from './homepage/HomepageContentCarousel'
import { HomepageFeaturedBlock } from './homepage/HomepageFeatured'
import { JustUpdatedSeriesBlock } from './homepage/JustUpdatedSeries'
import { LatestBlock } from './homepage/Latest'
import { NextUpBlock } from './homepage/NextUp'
import { PopularBlock } from './homepage/Popular'
import { RecommendedBlock } from './homepage/Recommended'
import { SeriesYouMightLikeBlock } from './homepage/SeriesYouMightLike'
import { TrendingSeriesBlock } from './homepage/TrendingSeries'
import { BenefitsBannerBlock } from './landing/BenefitsBanner'
import { LandingHeroBlock } from './landing/LandingHero'
import { LandingMemberQuoteBlock } from './landing/LandingMemberQuote'
import { LandingPerksBlock } from './landing/LandingPerks'
import { CollectThemAllBlock } from './landing/LandingCollectibles'
import { RainbowHeroBlock } from './landing/RainbowHero'
import { ScrollingCarouselBlock } from './landing/ScrollingCarousel'
import { ScrollingContentCarouselBlock } from './landing/ScrollingContentCarousel'
import { ProductGridBlock } from './ProductGrid'
import { PromoCodeBlock } from './PromoCode'
import { RedeemCollectableBlock } from './RedeemCollectable'
import { SpotifyEmbedBlock } from './Spotify'
import { CollectibleProgressBlock } from './CollectibleProgress'
import { MerchCollectiblesCallToActionBlock } from './MerchCollectiblesCallToAction'
import { LatestGridBlock } from './LatestGrid'

interface Props {
  blocks: (
    | PostContentBlock
    | PageContentBlock
    | HomepageContentBlock
    | LandingContentBlock
    | MerchPageContentBlock
    | AllSeriesContentBlocks
  )[]
  collectionId?: string
  collectionType?: CollectionType
  featuredPosts?: ContentCardProps[]
  textProps?: RichTextProps['textProps']
  slug?: string
  inContent?: boolean // Wether it's inside a "content" section, with its own horizontal padding
  landingHeroProps?: {
    videoRefreshing?: boolean
  }
}

const BlocksWrapper = styled(YStack, {
  name: 'BlocksWrapper',
  variants: {
    spaced: {
      true: {
        gap: '$7',
        $gtMd: {
          gap: '$8',
        },
      },
    },
  } as const,
})

type BlocksWrapperProps = GetProps<typeof BlocksWrapper>

export const RenderBlocks: React.FC<Props & BlocksWrapperProps> = ({
  blocks,
  collectionId,
  collectionType,
  featuredPosts = [],
  slug,
  textProps,
  inContent = false,
  landingHeroProps,
  ...props
}) => {
  const blocksFiltered = blocks.filter(
    (block) =>
      !('excludedPlatforms' in block) ||
      !block.excludedPlatforms ||
      !mapExcludedPlatforms(block.excludedPlatforms)?.includes(Platform.OS),
  )

  if (blocksFiltered.length === 0) return null

  return (
    <BlocksWrapper {...props}>
      {blocksFiltered.map((block, i) => {
        switch (block.__typename) {
          case 'Video':
            return <VideoBlock block={block} key={block.id} isUnlocked />

          case 'RichText':
            return (
              <RichTextBlock
                key={block.id}
                block={block}
                textProps={{ userSelect: 'all', ...textProps }}
              />
            )

          case 'ImageBlock':
          case 'ExternalImage':
            return <ImageBlock block={block} key={block.id} isExternal />

          case 'Banner':
            return (
              <BannerBlock block={block} key={block.id} paddedX={!inContent} />
            )

          case 'Faqs':
            return (
              <FAQsBlock block={block} key={block.id} standalone={!inContent} />
            )

          case 'Spotify':
            return <SpotifyEmbedBlock block={block} key={block.id} />

          case 'PromoCode':
            return <PromoCodeBlock block={block} key={block.id} slug={slug} />

          case 'HomepageFeatured':
            return <HomepageFeaturedBlock block={block} key={block.id} />

          case 'ContinueWatching':
            return <ContinueWatchingBlock block={block} key={block.id} />

          case 'Popular':
            return <PopularBlock block={block} key={block.id} />

          case 'NextUp':
            return <NextUpBlock block={block} key={block.id} />

          case 'Latest':
            return <LatestBlock block={block} key={block.id} />

          case 'ComingUpHomepage':
            return <ComingUpBlock block={block} key={block.id} />

          case 'TrendingSeries':
            return <TrendingSeriesBlock block={block} key={block.id} />

          case 'JustUpdatedSeries':
            return <JustUpdatedSeriesBlock block={block} key={block.id} />

          case 'Recommended':
            return <RecommendedBlock block={block} key={block.id} />

          case 'SeriesYouMightLike':
            return <SeriesYouMightLikeBlock block={block} key={block.id} />

          case 'PromoBanner':
            return null // display nothing here as the banner must be located in the MainLayout

          // TODO: Need to update the styles of this. It's used in collectibles page
          case 'CallToAction':
            return <CallToAction block={block} key={block.id} slug={slug} />

          case 'MerchCollectiblesCallToAction':
            return (
              <MerchCollectiblesCallToActionBlock
                block={block}
                key={block.id}
              />
            )

          case 'ImageCarousel':
            return <ImageCarouselBlock block={block} key={block.id} />

          case 'ProductGrid':
            return (
              <ProductGridBlock
                block={block}
                key={block.id}
                paddedX={!inContent}
                darkTheme={slug === 'merch'}
              />
            )

          case 'FormBlock':
            return <FormBlock block={block} key={block.id} />

          case 'LandingHero':
            return (
              <LandingHeroBlock
                block={block}
                key={block.id}
                videoRefreshing={landingHeroProps?.videoRefreshing}
              />
            )

          case 'RainbowHero':
            return <RainbowHeroBlock block={block} key={block.id} />

          case 'BenefitsBanner':
            return <BenefitsBannerBlock block={block} key={block.id} />

          case 'LandingMemberQuote':
            return <LandingMemberQuoteBlock block={block} key={block.id} />

          case 'ScrollingCarousel':
            return <ScrollingCarouselBlock block={block} key={block.id} />

          case 'ScrollingContentCarousel':
            return (
              <ScrollingContentCarouselBlock block={block} key={block.id} />
            )

          case 'LandingPerks':
            return <LandingPerksBlock block={block} key={block.id} />

          case 'LandingCollectibles':
            return <CollectThemAllBlock block={block} key={block.id} />

          case 'RedeemCollectable':
            return <RedeemCollectableBlock block={block} key={block.id} />

          case 'Button':
            return <ButtonBlock block={block} key={block.id} />

          case 'HomepageContentCarousel':
            return <HomepageContentCarouselBlock block={block} key={block.id} />

          case 'GifOfTheDayBlock':
            return <GifOfTheDayBlock block={block} key={block.id} />

          case 'CollectibleProgress':
            return <CollectibleProgressBlock block={block} key={block.id} />

          case 'LatestGrid':
            return <LatestGridBlock block={block} key={block.id} />

          default:
            return getEnv() === 'development' ? (
              <SizableText key={i} ta="center" px="$3.5" mx="auto">
                {JSON.stringify(block, null, 2)}
              </SizableText>
            ) : (
              <SizableText key={i} ff="$title" ta="center" px="$3.5" mx="auto">
                An app update is required to view this content.
              </SizableText>
            )
        }
      })}
    </BlocksWrapper>
  )
}
